import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { 
  fetchArticleById, 
  fetchArticleSources, 
  fetchFollowupAnswer 
} from '../utils/api';
import { 
  formatFriendlyTime, 
  capitalizeFirstLetter, 
  getCategoryClass,
  markArticleAsRead,
  trackEvent,
  trackError
} from '../utils/helpers';
import FollowupQuestions from './FollowupQuestions';

const ArticlePage = ({ articleProp = null, isModal = false }) => {
  const { id: urlId } = useParams();
  const location = useLocation();
  
  // Try to get article from props first, then navigation state, then URL params
  const preloadedArticle = articleProp || location.state?.article;
  
  // Get article ID from prop, URL, or preloaded article
  const id = articleProp?.id || urlId || preloadedArticle?.id;
  
  const [article, setArticle] = useState(preloadedArticle || null);
  const [sources, setSources] = useState([]);
  const [isLoading, setIsLoading] = useState(!preloadedArticle);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [error, setError] = useState(null);
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  const [expandedImageHeight, setExpandedImageHeight] = useState(280);
  const previousIdRef = useRef(null);

  useEffect(() => {
    const loadArticle = async () => {
      try {
        setError(null);
        
        // Guard clause: Don't proceed if ID is undefined
        if (!id) {
          setError('Artikeln kunde inte hittas - ogiltigt ID');
          return;
        }
        
        // Check if we're navigating to a different article
        const isNewArticle = previousIdRef.current !== id;
        
        if (isNewArticle) {
          previousIdRef.current = id;
        }
        
        // If we don't have preloaded article, fetch it
        if (!preloadedArticle) {
          setIsLoading(true);
          const articleData = await fetchArticleById(id);
          
          if (!articleData) {
            setError('Artikeln kunde inte hittas');
            return;
          }
          
          setArticle(articleData);
          setIsLoading(false);
          
          // Scroll to top after loading the new article (only if not in modal)
          if (isNewArticle && !isModal) {
            window.scrollTo(0, 0);
          }
        } else if (isNewArticle && !isModal) {
          // If we have preloaded article and it's a new article, scroll to top (only if not in modal)
          window.scrollTo(0, 0);
        }
        
        // Mark article as read (regardless of preloaded state)
        markArticleAsRead(parseInt(id));
        
        // Track article page view
        if (isNewArticle) {
          trackEvent('article-page-viewed', {
            articleId: id,
            source: preloadedArticle?.source || 'unknown',
            category: preloadedArticle?.category || 'unknown',
            isModal,
            wasPreloaded: !!preloadedArticle
          });
        }
        
        // Fetch missing data - avoid redundant calls
        setIsLoadingDetails(true);
        const promises = [];
        
        // Only fetch article data if we don't have preloaded data or need to update
        if (!preloadedArticle) {
          promises.push(fetchArticleById(id));
        } else {
          promises.push(Promise.resolve(preloadedArticle));
        }
        
        // Always fetch sources as they're not included in preloaded data
        promises.push(fetchArticleSources(id));
        
        const [fullArticleData, sourcesData] = await Promise.all(promises);
        
        // Update with full article data if we got it
        if (fullArticleData) {
          // Only preserve scroll position if we're updating the same article
          // (not when navigating to a different article) and not in modal
          if (!isNewArticle && !isModal) {
            // Save current scroll position before updating content
            const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
            
            setArticle(fullArticleData);
            
            // Restore scroll position after DOM update with multiple attempts for reliability
            requestAnimationFrame(() => {
              window.scrollTo(0, currentScrollTop);
              // Double-check after another frame to ensure it sticks
              requestAnimationFrame(() => {
                window.scrollTo(0, currentScrollTop);
              });
            });
          } else {
            setArticle(fullArticleData);
            // Scroll to top again after full article loads (in case we didn't have preloaded) - only if not in modal
            if (!preloadedArticle && !isModal) {
              window.scrollTo(0, 0);
            }
          }
        }
        
        setSources(sourcesData);
      } catch (err) {
        console.error('Error loading article:', err);
        trackError('article-details-load-error', err, {
          articleId: id,
          hasArticle: !!article,
          isModal
        });
        setError('Ett fel uppstod när artikeln skulle hämtas');
        setIsLoading(false);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    loadArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, preloadedArticle, isModal]);

  // Calculate optimal image height based on aspect ratio
  useEffect(() => {
    if (article?.image_url) {
      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        const screenWidth = window.innerWidth;
        const maxWidth = Math.min(screenWidth, 800); // Max width of article container
        
        // Calculate height based on aspect ratio
        let optimalHeight = maxWidth / aspectRatio;
        
        // Limit height between 200px and 400px
        optimalHeight = Math.max(200, Math.min(400, optimalHeight));
        
        setExpandedImageHeight(Math.round(optimalHeight));
      };
      img.src = article.image_url;
    }
  }, [article?.image_url]);

  if (isLoading && !article) {
    return (
      <div className="article-page">
        <div className="loading-container">
          <div className="loading-spinner">
            <svg viewBox="0 0 50 50" className="spinner">
              <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
            </svg>
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="article-page">
        <Link to="/" className="back-button">
          ← Tillbaka
        </Link>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2 className="error-title">{error || 'Artikeln kunde inte hittas'}</h2>
          <Link to="/" className="retry-button">
            Gå tillbaka till startsidan
          </Link>
        </div>
      </div>
    );
  }

  const categoryClass = getCategoryClass(article.category);

  // Toggle image expansion
  const toggleImageExpansion = () => {
    setIsImageExpanded(!isImageExpanded);
  };

  // Render all sources function
  const renderArticleSources = () => {
    if (!sources || sources.length === 0) {
      // Fallback to article data if no sources found
      if (!article) return null;
      
      return (
        <a 
          href={article.url} 
          className="source-link primary-source" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <span className="source-label">Originalkälla: {article.source}</span>
          <span className="source-title">"{article.original_title}"</span>
          <span className="read-more">Läs hela artikeln →</span>
        </a>
      );
    }
    
    const primarySource = sources.find(s => s.is_primary) || sources[0];
    const otherSources = sources.filter(s => !s.is_primary && s.id !== primarySource.id);
    
    return (
      <div className="article-sources-section">
        <div className="sources-header">
          <span className="sources-label">
            {sources.length > 1 ? `Originalkällor (${sources.length})` : 'Originalkälla'}
          </span>
        </div>
        
        {/* Primary source */}
        <a 
          href={primarySource.url} 
          className="source-link primary-source" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <span className="source-label">{primarySource.source}</span>
          <span className="source-title">"{primarySource.original_title}"</span>
          <span className="read-more">Läs hela artikeln →</span>
        </a>
        
        {/* Other sources */}
        {otherSources.map((source, index) => (
          <a 
            key={source.id || index}
            href={source.url} 
            className="source-link secondary-source" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <span className="source-label">{source.source}</span>
            <span className="source-title">"{source.original_title}"</span>
            <span className="read-more">Läs hela artikeln →</span>
          </a>
        ))}
      </div>
    );
  };

  return (
    <div className="article-page">

      {article.image_url && (
        <div 
          className={`article-image-container ${isImageExpanded ? 'expanded' : ''}`}
          onClick={toggleImageExpansion}
          style={{
            backgroundImage: `url(${article.image_url})`,
            height: isImageExpanded ? `${expandedImageHeight}px` : '100px'
          }}
        >
          {article.image_photographer && (
            <div className="image-credit">
              Foto: <a 
                href={article.image_photographer_url} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {article.image_photographer}
              </a> på <a 
                href="https://unsplash.com/?utm_source=spanska_nyheter&utm_medium=referral"
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                Unsplash
              </a>
            </div>
          )}
        </div>
      )}
      
      <article className="article-detail">
        
        <header className="article-header">
          <div className={`category-tag ${categoryClass}`}>
            {capitalizeFirstLetter(article.category)}
          </div>
          <div className="article-detail-meta" style={{ marginBottom: '5px' }}>
            {article.region && article.region.trim() && (
              <span className="region">{article.region}</span>
            )}
          </div>
          <h1 className="article-detail-title">
            {article.swedish_title || article.original_title}
          </h1>
        </header>
      </article>

      <div className="article-content">
        {article.full_summary && (
          <div className="article-content-section">
            <div className="article-body" dangerouslySetInnerHTML={{ __html: article.full_summary }} />
          </div>
        )}
        <div className="article-detail-meta" style={{ padding: '8px 0' }}>
          <div className="time">{formatFriendlyTime(article.inserted_datetime)}</div>
          <div className="source">{article.source}</div>
        </div>

        {/* Show loading indicator while fetching details */}
        {isLoadingDetails && !article.full_summary && (
          <div className="article-content-section">
            <div className="loading-details">
              <div className="loading-spinner small">
                <svg viewBox="0 0 50 50" className="spinner">
                  <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                </svg>
              </div>
              <span>Laddar artikelinnehåll...</span>
            </div>
          </div>
        )}
      </div>


      {/* AI Followup Questions */}
      {article.followup_questions && article.followup_questions.length > 0 && id && (
        <FollowupQuestions 
          questions={article.followup_questions}
          articleId={id}
          onFetchAnswer={fetchFollowupAnswer}
        />
      )}

      {/* Source links */}
      {renderArticleSources()}

    </div>
  );
};

export default ArticlePage;
